import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://igqds3armu.us-east-1.awsapprunner.com/api',
    withCredentials: true,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    },
    timeout: 30000, // 30 second timeout
    maxRedirects: 5
});

// Create a fake response for auth/me when not authenticated
const createFakeAuthResponse = () => {
    return Promise.resolve({
        data: {
            status: 'success',
            data: { user: null }
        }
    });
};

// Check if a request is an auth request
const isAuthRequest = (url) => {
    return url && (
        url.includes('/auth/me') || 
        url.includes('/auth/refresh-token')
    );
};

// Add request interceptor
api.interceptors.request.use(
    (config) => {
        // Get token from localStorage as fallback
        const token = localStorage.getItem('token');
        
        // Add Authorization header if token exists
        if (token && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Ensure credentials are included
        config.withCredentials = true;

        console.debug('[API] Making request:', {
            url: config.url,
            method: config.method,
            withCredentials: config.withCredentials,
            hasToken: !!token,
            headers: {
                ...config.headers,
                Authorization: config.headers.Authorization ? 'Bearer [hidden]' : undefined
            }
        });

        // Remove CORS headers from client-side requests
        // These should be set by the server, not the client
        delete config.headers['Access-Control-Allow-Origin'];
        delete config.headers['Access-Control-Allow-Credentials'];

        // Check if we're on the login page
        const isLoginPage = window.location.pathname === '/login' || window.isOnLoginPage === true;
        
        // For auth requests on login page, return fake response
        if (isAuthRequest(config.url) && isLoginPage) {
            if (config.url.includes('/auth/me')) {
                console.debug('[API] Returning fake response for auth/me - on login page');
                return {
                    ...config,
                    validateStatus: () => false,
                    __FAKE_NO_TOKEN_RESPONSE__: true
                };
            }
            
            // Cancel other auth requests if on login page
            console.debug('[API] Cancelling auth request - on login page');
            const controller = new AbortController();
            controller.abort();
            config.signal = controller.signal;
        }

        return config;
    },
    (error) => {
        console.error('[API] Request error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor
api.interceptors.response.use(
    (response) => {
        console.debug('[API] Response:', {
            url: response.config.url,
            status: response.status
        });
        return response;
    },
    async (error) => {
        console.error('[API] Response error:', {
            url: error.config?.url,
            status: error.response?.status,
            message: error.response?.data?.message
        });

        // Handle rate limiting with exponential backoff retry
        if (error.response?.status === 429 && !error.config.__isRetryRequest) {
            const retryCount = error.config.__retryCount || 0;
            
            // Only retry up to 3 times
            if (retryCount < 3) {
                console.debug(`[API] Rate limited, retrying (${retryCount + 1}/3)...`);
                
                // Exponential backoff: 1s, 2s, 4s
                const delay = Math.pow(2, retryCount) * 1000;
                
                // Wait for the delay
                await new Promise(resolve => setTimeout(resolve, delay));
                
                // Update retry count and flag
                const newConfig = {
                    ...error.config,
                    __isRetryRequest: true,
                    __retryCount: retryCount + 1
                };
                
                // Retry the request
                return api(newConfig);
            }
        }

        // Handle auth errors
        if (error.response?.status === 401) {
            console.debug('[API] Authentication error detected');
            
            // Don't try to refresh if this is a refresh token request or login request
            if (error.config.url.includes('/auth/refresh-token') || error.config.url.includes('/auth/login')) {
                console.debug('[API] Not attempting refresh for auth endpoints');
                return Promise.reject(error);
            }
            
            // Try to refresh the token
            try {
                console.debug('[API] Attempting to refresh token...');
                const response = await authApi.refreshToken();
                if (response.data?.status === 'success') {
                    console.debug('[API] Token refreshed successfully');
                    // Retry the original request
                    return api(error.config);
                }
            } catch (refreshError) {
                console.error('[API] Token refresh failed:', refreshError);
                // Clear any stored tokens
                localStorage.removeItem('token');
                // Dispatch auth error event
                window.dispatchEvent(new CustomEvent('auth-error'));
            }
        }

        return Promise.reject(error);
    }
);

// Products API
export const productsApi = {
    getAll: () => api.get('/products'),
    getById: (id) => api.get(`/products/${id}`),
    create: (data) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Accept': 'application/json'
            },
            withCredentials: true,
            crossDomain: true
        };
        console.debug('[API] Creating product with config:', config);
        return api.post('/products', data, config);
    },
    update: (id, data) => {
        const config = {
            headers: {
                'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json',
                'Accept': 'application/json'
            },
            withCredentials: true,
            crossDomain: true
        };
        console.debug('[API] Updating product with config:', config);
        return api.put(`/products/${id}`, data, config);
    },
    updateStock: (id, stock) => {
        return api.put(`/products/${id}/stock`, { stock });
    },
    delete: (id) => api.delete(`/products/${id}`),
};

// Categories API
export const categoriesApi = {
    getAll: () => api.get('/categories'),
    getById: (id) => api.get(`/categories/${id}`),
    create: (data) => api.post('/categories', data),
    update: (id, data) => api.put(`/categories/${id}`, data),
    delete: (id) => api.delete(`/categories/${id}`),
};

// Stock Transactions API
export const stockApi = {
    getAll: () => api.get('/stock'),
    getById: (id) => api.get(`/stock/${id}`),
    create: (data) => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            withCredentials: true,
            crossDomain: true
        };
        return api.post('/stock', data, config);
    },
    update: (id, data) => api.put(`/stock/${id}`, data),
    delete: (id) => api.delete(`/stock/${id}`),
    getHistory: (params) => api.get('/stock/history', { params }),
};

// Auth API
export const authApi = {
    login: (credentials) => api.post('/auth/login', credentials),
    me: () => {
        // Check if we're on the login page
        const isLoginPage = window.location.pathname === '/login' || window.isOnLoginPage === true;
        if (isLoginPage) {
            console.debug('[API] Skipping /auth/me request - on login page');
            return createFakeAuthResponse();
        }
        
        return api.get('/auth/me').catch(error => {
            // Handle 401 errors here as well for extra safety
            if (error.response?.status === 401) {
                console.debug('[API] Auth error caught in me() method');
                return createFakeAuthResponse();
            }
            throw error;
        });
    },
    logout: () => api.post('/auth/logout'),
    refreshToken: () => api.post('/auth/refresh-token'),
    getUsers: () => api.get('/auth/users'),
    deleteUser: (id) => api.delete(`/auth/users/${id}`)
};

// Reports API
export const reportsApi = {
    getStockSummary: (params) => api.get('/reports/stock/summary', { params }),
    getStockDetail: (params) => api.get('/reports/stock/detail', { params }),
};

export default api; 